import React, { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { MENU_ID_PROJECTS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { useSidebarNav4 } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import type { Nav4DragAndDropMenuItemProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ActionButtons } from '../../common/ui/action-buttons/ActionButtons.tsx';
import type { Nav4Projects$key } from './__generated__/Nav4Projects.graphql';
import { Nav4ProjectsDefault } from './default/Nav4ProjectsDefault.tsx';
import { Nav4ProjectsForLanding } from './for-landing/Nav4ProjectsForLanding.tsx';

export type Nav4ProjectsProps = Nav4DragAndDropMenuItemProps & {
	isExpanded: boolean;
	queryRef: Nav4Projects$key;
};

export function Nav4Projects(props: Nav4ProjectsProps): React.JSX.Element {
	const { isExpanded, queryRef, ...dndProps } = props;
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment<Nav4Projects$key>(
		graphql`
			fragment Nav4Projects on Query
			@argumentDefinitions(
				currentURL: { type: URL }
				isAllowingEndUsersToAccessCustomTemplates: {
					type: "Boolean!"
					provider: "@jira/platform__graphql-relay-provider/src/jira-custom-template-allow-end-users-to-create-custom-templates.relayprovider"
				}
			) {
				...Nav4ProjectsForLanding
					@dangerously_unaliased_fixme
					@include(if: $isProjectsExpanded)
					@arguments(currentURL: $currentURL)
				jira {
					hasIsProjectsExpanded: __typename @include(if: $isProjectsExpanded)
					canCreateProject: canPerform(type: CREATE_PROJECT, cloudId: $cloudId)
						@optIn(to: "JiraAction")
					shouldDisplay: canPerform(type: VIEW_PROJECT_CREATION_ENTRY, cloudId: $cloudId)
						@include(if: $isAllowingEndUsersToAccessCustomTemplates)
						@optIn(to: "JiraAction")
				}
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		queryRef,
	);

	const { isInitialSelectedPath } = useSidebarNav4();

	const canCreateProject = !!data?.jira?.canCreateProject;
	const shouldDisplayCreateProject = !!data?.jira?.shouldDisplay;
	const actionButtons = useMemo(
		() => (
			<ActionButtons
				canCreateProject={canCreateProject}
				{...(fg('allow_end_users_to_access_custom_templates')
					? { shouldDisplayCreateProject }
					: {})}
			/>
		),
		[canCreateProject, shouldDisplayCreateProject],
	);

	const isExpandedValue = fg('navx-global-side-nav')
		? !!dndProps.isExpandOrFlyoutOpen && isExpanded
		: isExpanded;

	if (!data.jira?.hasIsProjectsExpanded && isInitialSelectedPath(MENU_ID_PROJECTS)) {
		// We need to make sure that we're only passing the conditionalal fragment when it is actually
		// available. Due to a lack of support the @alias Relay directive, we need to use another mechanism
		// and in this case we're using a hardcoded __typename to check if the fragment is available.
		// This is a workaround until we can use the @alias directive in Relay. If the hasIsProjectsExpanded
		// attribute is not available, then the fragment won't be either and we shouldn't pass it.
		return (
			<Nav4ProjectsDefault
				actionsOnHover={actionButtons}
				isExpanded={isExpandedValue}
				{...dndProps}
			/>
		);
	}

	return isInitialSelectedPath(MENU_ID_PROJECTS) ? (
		<Nav4ProjectsForLanding
			actionsOnHover={actionButtons}
			isExpanded={isExpandedValue}
			queryRef={data}
			{...dndProps}
		/>
	) : (
		<Nav4ProjectsDefault actionsOnHover={actionButtons} isExpanded={isExpanded} {...dndProps} />
	);
}
